import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  WhatsApp,
  RocketOutlined,
  EmojiObjectsOutlined,
} from "@material-ui/icons";
import { isOnboardingFlagSet, setOnboardingFlag } from "../../utils/onboardingFlags";

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    maxWidth: 460,
  },
  header: {
    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    padding: theme.spacing(4, 3, 3),
    textAlign: "center",
    color: "#fff",
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    fontSize: "1.4rem",
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    opacity: 0.9,
    fontSize: "0.9rem",
  },
  content: {
    padding: theme.spacing(3),
  },
  stepList: {
    listStyle: "none",
    padding: 0,
    margin: theme.spacing(2, 0),
  },
  stepItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    color: "#fff",
    fontSize: "0.75rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 2,
  },
  stepText: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  stepDesc: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(2, 3),
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  btnPrimary: {
    borderRadius: 8,
    fontWeight: 600,
    padding: theme.spacing(1.2, 0),
  },
  btnSecondary: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
}));

const STEPS = [
  {
    title: "Suas filas de exemplo já estão prontas",
    desc: "Criamos as filas Suporte e Vendas pra você começar. Personalize ou crie novas em Filas quando quiser.",
  },
  {
    title: "Conecte seu WhatsApp e vincule a fila",
    desc: "Escaneie o QR Code e, na mesma tela, vincule uma fila à conexão. Esse vínculo é essencial para o sistema funcionar.",
  },
  {
    title: "Adicione atendentes e comece!",
    desc: "Cadastre sua equipe, associe às filas e pronto — seu atendimento via WhatsApp estará operacional.",
  },
];

const STORAGE_KEY = "me_welcome_modal_seen";

const WelcomeModal = ({ onGuide, isNewAccount = null } = {}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Já visto (chave por conta ou chave global antiga) — nunca reexibe.
    if (isOnboardingFlagSet(STORAGE_KEY)) return;
    // Conta antiga/já configurada: marca como visto e não mostra.
    if (isNewAccount === false) {
      setOnboardingFlag(STORAGE_KEY);
      return;
    }
    // Conta nova: mostra. Enquanto o status carrega (null), aguarda.
    if (isNewAccount === true) setOpen(true);
  }, [isNewAccount]);

  const handleClose = (guide) => {
    setOnboardingFlag(STORAGE_KEY);
    setOpen(false);
    if (guide && onGuide) onGuide();
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      classes={{ paper: classes.paper }}
      disableBackdropClick
    >
      <div className={classes.header}>
        <div className={classes.iconCircle}>
          <WhatsApp style={{ fontSize: 32 }} />
        </div>
        <Typography className={classes.title}>
          Bem-vindo ao Message Express! 🎉
        </Typography>
        <Typography className={classes.subtitle}>
          Você tem <strong>7 dias grátis</strong> para explorar tudo.
          <br />
          Veja como começar em 3 passos simples:
        </Typography>
      </div>

      <DialogContent className={classes.content}>
        <ul className={classes.stepList}>
          {STEPS.map((step, i) => (
            <li key={i} className={classes.stepItem}>
              <span className={classes.stepNumber}>{i + 1}</span>
              <div>
                <Typography className={classes.stepText}>
                  <strong>{step.title}</strong>
                </Typography>
                <Typography className={classes.stepDesc}>
                  {step.desc}
                </Typography>
              </div>
            </li>
          ))}
        </ul>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          className={classes.btnPrimary}
          onClick={() => handleClose(true)}
          startIcon={<EmojiObjectsOutlined />}
        >
          Me guia nos primeiros passos
        </Button>
        <Button
          fullWidth
          className={classes.btnSecondary}
          onClick={() => handleClose(false)}
        >
          Explorar por conta própria
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeModal;
